import { useEffect, useState } from "react";
import { X, Rocket } from "lucide-react";
import EmailCaptureForm from "@/components/EmailCaptureForm";

const STORAGE_KEY = "rl_exit_intent_seen_v1";
const ARM_DELAY_MS = 6000; // don't fire in the first 6s — avoids accidental open

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = !!localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      // localStorage disabled (e.g. private mode) — treat as unseen.
      console.warn("ExitIntentModal: localStorage unavailable on read.", err?.name);
    }
    if (seen) return;

    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, ARM_DELAY_MS);

    const onMouseOut = (e) => {
      if (!armed) return;
      // Only trigger when cursor leaves the top of the viewport
      if (e.clientY > 0) return;
      if (e.relatedTarget || e.toElement) return;
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch (err) {
        console.warn("ExitIntentModal: localStorage unavailable on write.", err?.name);
      }
      setOpen(true);
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-900/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div className="relative w-full max-w-md bg-white border-t-[6px] border-[#EF4444] shadow-2xl">
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="exit-intent-close"
          className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-zinc-900"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-6 h-6 bg-[#002FA7] text-white">
              <Rocket className="w-3.5 h-3.5" strokeWidth={2.25} />
            </span>
            <span className="font-mono uppercase tracking-wider text-[10px] text-zinc-500">Before you go</span>
          </div>
          <h2 id="exit-intent-title" className="font-display font-extrabold text-2xl tracking-tight text-zinc-900">
            Get the recruiter-grade resume kit, free.
          </h2>
          <p className="mt-2 text-sm text-zinc-500">
            Drop your email and we'll send you the 2026 Resume Trends Report — the same playbook our top-scoring candidates use to land callbacks.
          </p>
          <div className="mt-5">
            <EmailCaptureForm
              source="exit_intent"
              testIdPrefix="exit-intent-capture"
              variant="light"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
